import { type ApolloClientOptions, ApolloLink } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { HttpLink } from 'apollo-angular/http';
import { AUTH_TYPE, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { GraphQLFormattedError } from 'graphql';

export interface ApolloLinkFactoryOptions {
	httpLink: HttpLink;
	endpoint: string;
	awsRegion: string;
	identityPoolId: string;
	linkOptions?: Partial<ApolloClientOptions<any>>; // Passthrough
	/**
	 * The error handler.
	 */
	handleError: (message: string, error: Error | GraphQLFormattedError) => void;
}

export function createApolloLink({
	httpLink,
	endpoint,
	awsRegion,
	identityPoolId,
	handleError,
}: ApolloLinkFactoryOptions): ApolloLink {
	const httpLinkHandler = httpLink.create({
		uri: endpoint,
	});

	const authLinkOptions: Parameters<typeof createAuthLink>[0] = {
		url: endpoint,
		region: awsRegion,
		auth: {
			type: AUTH_TYPE.AWS_IAM,
			credentials: fromCognitoIdentityPool({
				identityPoolId,
				clientConfig: { region: awsRegion },
			}),
		},
	};

	return ApolloLink.from([
		createErrorHandlerLink(handleError),
		new RetryLink({
			delay: { initial: 300, max: 1000, jitter: true },
			attempts: { max: 5 },
		}),
		// TODO: Find / write replacements for these as they're deprecated / unmaintained.
		createAuthLink(authLinkOptions),
		createSubscriptionHandshakeLink(authLinkOptions, httpLinkHandler),
	]);
}

/**
 * This link will raise any errors occurring while executing graphql operations.
 * TODO: Check how this works with observables. We use similar in cas-client but everything is converted to observables.
 */
const createErrorHandlerLink = (
	logError: (message: string, error: Error | GraphQLFormattedError) => void,
) =>
	onError(({ graphQLErrors, networkError, operation }) => {
		if (graphQLErrors) {
			for (const graphQLError of graphQLErrors) {
				logError(`[GraphQL error] (${operation.operationName}): ${graphQLError.message}`, graphQLError);
			}
		}

		if (networkError) {
			logError(`[Network error] (${operation.operationName}): ${networkError.message}`, networkError);
		}
	});
